import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import Button from '../../common/Button';
import DiagnosisDropdown from '../../common/DiagnosisDropdown';
import Modal from '../../common/Modal';
import ToastContainer from '../../common/ToastContainer';
import { useToast } from '../../../hooks/useToast';
import PatientService from '../../../services/patientService';
import PatientOdontogramService from '../../../services/patientOdontogramService';
import DiagnosisService from '../../../services/diagnosisService';
import PatientSectionHeader from '../common/PatientSectionHeader';
import Odontogram from './Odontogram';
import { getSurfaceName, SURFACE_KEYS } from '../../../utils/dentalSurfaceUtils';
import './NewDentalNote.css';
import './DiagnosisStyles.css';
import './OdontogramTabs.css';

const SURFACES = SURFACE_KEYS;

const OdontogramTabs = () => {
  const { patientId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const auth = useAuth();
  const { toasts, removeToast, success, error, warning } = useToast();

  // Read active tab from navigation state or default to 'initial'
  const [activeTab, setActiveTab] = useState(location.state?.activeTab || 'initial'); // 'initial' | 'evolution' | 'discharge'
  const [mode, setMode] = useState('adult');
  const [patient, setPatient] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [odontogramData, setOdontogramData] = useState(null);

  // State for initial odontogram
  const [initialSelected, setInitialSelected] = useState({});
  const [initialSelectedDiagnosis, setInitialSelectedDiagnosis] = useState(null);
  const [initialCurrentNoteText, setInitialCurrentNoteText] = useState('');
  const [initialGeneralNotes, setInitialGeneralNotes] = useState('');
  const [initialAddedSelections, setInitialAddedSelections] = useState([]);

  // State for evolution odontogram
  const [evolutionSelected, setEvolutionSelected] = useState({});
  const [evolutionSelectedDiagnosis, setEvolutionSelectedDiagnosis] = useState(null);
  const [evolutionCurrentNoteText, setEvolutionCurrentNoteText] = useState('');
  const [evolutionGeneralNotes, setEvolutionGeneralNotes] = useState('');
  const [evolutionAddedSelections, setEvolutionAddedSelections] = useState([]);

  // State for discharge history
  const [dischargeHistory, setDischargeHistory] = useState([]);
  const [showDischargeConfirmModal, setShowDischargeConfirmModal] = useState(false);

  // Load patient
  useEffect(() => {
    const loadPatient = async () => {
      if (patientId && auth.user?.access_token) {
        try {
          const patientData = await PatientService.getPatientById(patientId, auth.user.access_token);
          setPatient(patientData);
        } catch (error) {
          console.error('Error loading patient:', error);
        }
      }
    };
    loadPatient();
  }, [patientId, auth.user?.access_token]);

  // Load patient odontogram
  const loadOdontogram = async (silent = false) => {
    if (!patientId || !auth.user?.access_token) return;

    try {
      if (!silent) {
        setLoading(true);
      }
      const data = await PatientOdontogramService.getPatientOdontogram(patientId, auth.user.access_token);
      setOdontogramData(data);
      setMode(data.mode || 'adult');

      // Load the diagnosis catalog once for both odontograms
      const hasSubNotes = (data.initialOdontogram?.subNotes?.length ?? 0) > 0
        || (data.evolutionOdontogram?.subNotes?.length ?? 0) > 0;
      const diagnosesById = hasSubNotes
        ? await DiagnosisService.getDiagnosesMap(auth.user.access_token).catch(error => {
            console.error('Error loading diagnoses:', error);
            return new Map();
          })
        : new Map();

      // Load initial odontogram data
      loadOdontogramData(data.initialOdontogram, 'initial', diagnosesById);

      // Load evolution odontogram data
      loadOdontogramData(data.evolutionOdontogram, 'evolution', diagnosesById);

      // Load discharge history (newest first)
      const history = (data.dischargeHistory || []).slice();
      history.sort((a, b) => new Date(b.date) - new Date(a.date));
      setDischargeHistory(history);
    } catch (error) {
      console.error('Error loading odontogram:', error);
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    loadOdontogram();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId, auth.user?.access_token]);

  // Load odontogram data and convert to UI format
  const loadOdontogramData = (odontogramData, type, diagnosesById) => {
    if (!odontogramData || !odontogramData.subNotes || odontogramData.subNotes.length === 0) {
      return;
    }

    const loadedSelections = [];

    try {
      odontogramData.subNotes.forEach((subNote, index) => {
        const diagnosis = diagnosesById.get(subNote.diagnosisId) || {
          id: subNote.diagnosisId,
          name: 'Diagnóstico no encontrado',
          color: '#gray',
          isCompleteTooth: false
        };

        // Convert selections to odontogram format
        const convertedSelections = {};
        if (subNote.selections && Array.isArray(subNote.selections)) {
          subNote.selections.forEach(selection => {
            const toothLabel = selection.tooth;
            const surface = denormalizeSurface(selection.surface);
            const colorId = selection.colorId || selection.color || diagnosis.color;

            if (!convertedSelections[toothLabel]) {
              convertedSelections[toothLabel] = {};
            }
            convertedSelections[toothLabel][surface] = colorId;
          });
        }

        loadedSelections.push({
          id: Date.now() + index,
          diagnosis: diagnosis,
          selections: convertedSelections,
          noteText: subNote.text || ''
        });
      });

      if (type === 'initial') {
        setInitialAddedSelections(loadedSelections);
        setInitialGeneralNotes(odontogramData.text || '');
      } else if (type === 'evolution') {
        setEvolutionAddedSelections(loadedSelections);
        setEvolutionGeneralNotes(odontogramData.text || '');
      }
    } catch (error) {
      console.error('Error loading odontogram data:', error);
    }
  };

  const denormalizeSurface = (s) => {
    const map = {
      'oclusal/incisal': 'top',
      'distal': 'right',
      'lingual/palatino': 'bottom',
      'mesial': 'left',
      'centro': 'center',
    };
    return map[s] || s;
  };

  const normalizeSurface = (s) => {
    const map = {
      top: 'oclusal/incisal',
      right: 'distal',
      bottom: 'lingual/palatino',
      left: 'mesial',
      center: 'centro',
    };
    return map[s] || s;
  };

  const goBack = () => {
    navigate(`/patients/${patientId}`);
  };

  // Check if there is SAVED data in the backend (for discharge and mode lock)
  const hasSavedOdontogramData = () => {
    if (!odontogramData) return false;
    const hasInitial = (odontogramData.initialOdontogram?.subNotes?.length ?? 0) > 0;
    const hasEvolution = (odontogramData.evolutionOdontogram?.subNotes?.length ?? 0) > 0;
    return hasInitial || hasEvolution;
  };

  // Change mode (adult/pediatric) - only when no saved data
  const handleModeChange = (newMode) => {
    if (hasSavedOdontogramData()) {
      warning('No puedes cambiar el modo una vez que has guardado selecciones en el odontograma.');
      return;
    }
    setMode(newMode);
  };

  // === INITIAL ODONTOGRAM HANDLERS ===
  const toggleInitialSelection = (toothLabel, surface) => {
    if (!SURFACES.includes(surface)) return;
    if (!initialSelectedDiagnosis) return;

    const isInAddedSelections = initialAddedSelections.some(selection =>
      selection.selections[toothLabel] && selection.selections[toothLabel][surface]
    );

    if (isInAddedSelections) return;

    setInitialSelected(prev => {
      const toothData = prev[toothLabel] || {};
      const next = { ...prev };

      if (initialSelectedDiagnosis.isCompleteTooth) {
        const hasAddedSurfaces = SURFACES.some(s =>
          initialAddedSelections.some(selection =>
            selection.selections[toothLabel] && selection.selections[toothLabel][s]
          )
        );

        if (hasAddedSurfaces) return prev;

        if (toothData[surface]) {
          delete next[toothLabel];
        } else {
          const allSurfaces = {};
          SURFACES.forEach(s => {
            allSurfaces[s] = initialSelectedDiagnosis.color;
          });
          next[toothLabel] = allSurfaces;
        }
      } else {
        if (toothData[surface]) {
          const newToothData = { ...toothData };
          delete newToothData[surface];

          if (Object.keys(newToothData).length === 0) {
            delete next[toothLabel];
          } else {
            next[toothLabel] = newToothData;
          }
        } else {
          next[toothLabel] = {
            ...toothData,
            [surface]: initialSelectedDiagnosis.color
          };
        }
      }

      return next;
    });
  };

  const handleAddInitialSelection = () => {
    if (!initialSelectedDiagnosis || Object.keys(initialSelected).length === 0) return;

    const newSelection = {
      id: Date.now(),
      diagnosis: initialSelectedDiagnosis,
      selections: { ...initialSelected },
      noteText: initialCurrentNoteText
    };

    setInitialAddedSelections(prev => [...prev, newSelection]);
    setInitialSelected({});
    setInitialCurrentNoteText('');
  };

  const handleRemoveInitialSelection = (selectionId) => {
    setInitialAddedSelections(prev => prev.filter(sel => sel.id !== selectionId));
  };

  const handleSaveInitial = async () => {
    try {
      setIsSaving(true);

      const subNotes = initialAddedSelections.map(selection => {
        const selections = [];
        Object.entries(selection.selections).forEach(([toothLabel, surfaceData]) => {
          Object.entries(surfaceData).forEach(([surface, color]) => {
            selections.push({
              tooth: toothLabel,
              surface: normalizeSurface(surface),
              color: color,
              colorId: color
            });
          });
        });

        return {
          diagnosisId: selection.diagnosis.id,
          selections: selections,
          text: selection.noteText || ''
        };
      });

      const data = {
        subNotes: subNotes,
        text: initialGeneralNotes || ''
      };

      await PatientOdontogramService.updateInitialOdontogram(patientId, data, auth.user.access_token);
      
      // Persist mode on backend
      await PatientOdontogramService.updateMode(patientId, mode, auth.user.access_token);
      
      // Reload odontogram to reflect synced changes (silent)
      await loadOdontogram(true);
      
      success('Odontograma guardado');
    } catch (err) {
      console.error('Error saving initial odontogram:', err);
      error('Error al guardar');
    } finally {
      setIsSaving(false);
    }
  };

  // === EVOLUTION ODONTOGRAM HANDLERS ===
  const toggleEvolutionSelection = (toothLabel, surface) => {
    if (!SURFACES.includes(surface)) return;
    if (!evolutionSelectedDiagnosis) return;

    const isInAddedSelections = evolutionAddedSelections.some(selection =>
      selection.selections[toothLabel] && selection.selections[toothLabel][surface]
    );

    if (isInAddedSelections) return;

    setEvolutionSelected(prev => {
      const toothData = prev[toothLabel] || {};
      const next = { ...prev };

      if (evolutionSelectedDiagnosis.isCompleteTooth) {
        const hasAddedSurfaces = SURFACES.some(s =>
          evolutionAddedSelections.some(selection =>
            selection.selections[toothLabel] && selection.selections[toothLabel][s]
          )
        );

        if (hasAddedSurfaces) return prev;

        if (toothData[surface]) {
          delete next[toothLabel];
        } else {
          const allSurfaces = {};
          SURFACES.forEach(s => {
            allSurfaces[s] = evolutionSelectedDiagnosis.color;
          });
          next[toothLabel] = allSurfaces;
        }
      } else {
        if (toothData[surface]) {
          const newToothData = { ...toothData };
          delete newToothData[surface];

          if (Object.keys(newToothData).length === 0) {
            delete next[toothLabel];
          } else {
            next[toothLabel] = newToothData;
          }
        } else {
          next[toothLabel] = {
            ...toothData,
            [surface]: evolutionSelectedDiagnosis.color
          };
        }
      }

      return next;
    });
  };

  const handleAddEvolutionSelection = () => {
    if (!evolutionSelectedDiagnosis || Object.keys(evolutionSelected).length === 0) return;

    const newSelection = {
      id: Date.now(),
      diagnosis: evolutionSelectedDiagnosis,
      selections: { ...evolutionSelected },
      noteText: evolutionCurrentNoteText
    };

    setEvolutionAddedSelections(prev => [...prev, newSelection]);
    setEvolutionSelected({});
    setEvolutionCurrentNoteText('');
  };

  const handleRemoveEvolutionSelection = (selectionId) => {
    setEvolutionAddedSelections(prev => prev.filter(sel => sel.id !== selectionId));
  };

  const handleSaveEvolution = async () => {
    try {
      setIsSaving(true);

      const subNotes = evolutionAddedSelections.map(selection => {
        const selections = [];
        Object.entries(selection.selections).forEach(([toothLabel, surfaceData]) => {
          Object.entries(surfaceData).forEach(([surface, color]) => {
            selections.push({
              tooth: toothLabel,
              surface: normalizeSurface(surface),
              color: color,
              colorId: color
            });
          });
        });

        return {
          diagnosisId: selection.diagnosis.id,
          selections: selections,
          text: selection.noteText || ''
        };
      });

      const data = {
        subNotes: subNotes,
        text: evolutionGeneralNotes || ''
      };

      await PatientOdontogramService.updateEvolutionOdontogram(patientId, data, auth.user.access_token);
      
      // Persist mode on backend
      await PatientOdontogramService.updateMode(patientId, mode, auth.user.access_token);
      
      success('Odontograma guardado');
    } catch (err) {
      console.error('Error saving evolution odontogram:', err);
      error('Error al guardar');
    } finally {
      setIsSaving(false);
    }
  };

  // === DISCHARGE PATIENT HANDLER ===
  const handleDischargePatient = async () => {
    if (!hasSavedOdontogramData()) {
      warning('No hay datos guardados en el odontograma para dar de alta.');
      return;
    }
    try {
      setIsSaving(true);
      await PatientOdontogramService.dischargePatient(patientId, auth.user.access_token);
      
      // Reload odontogram (silent)
      await loadOdontogram(true);
      
      // Stay on discharge tab to show updated history
      
      // Clear local state
      setInitialAddedSelections([]);
      setEvolutionAddedSelections([]);
      setInitialGeneralNotes('');
      setEvolutionGeneralNotes('');
      
      setShowDischargeConfirmModal(false);
      success('Paciente dado de alta');
    } catch (err) {
      console.error('Error discharging patient:', err);
      error('Error al dar de alta');
    } finally {
      setIsSaving(false);
    }
  };

  // Merge selections for odontogram display
  const getAllSelections = (type) => {
    const combined = {};
    const addedSelections = type === 'initial' ? initialAddedSelections : evolutionAddedSelections;
    const selected = type === 'initial' ? initialSelected : evolutionSelected;

    addedSelections.forEach(selection => {
      Object.entries(selection.selections).forEach(([toothLabel, surfaceData]) => {
        if (!combined[toothLabel]) {
          combined[toothLabel] = {};
        }
        Object.entries(surfaceData).forEach(([surface, color]) => {
          combined[toothLabel][surface] = color;
        });
      });
    });

    Object.entries(selected).forEach(([toothLabel, surfaceData]) => {
      if (!combined[toothLabel]) {
        combined[toothLabel] = {};
      }
      Object.entries(surfaceData).forEach(([surface, color]) => {
        combined[toothLabel][surface] = color;
      });
    });

    return combined;
  };

  const getDiagnosisMap = (type) => {
    const toothSurfaceToDiagnosis = {};
    const addedSelections = type === 'initial' ? initialAddedSelections : evolutionAddedSelections;
    const selected = type === 'initial' ? initialSelected : evolutionSelected;
    const selectedDiagnosis = type === 'initial' ? initialSelectedDiagnosis : evolutionSelectedDiagnosis;

    addedSelections.forEach(selection => {
      if (selection.diagnosis) {
        Object.entries(selection.selections).forEach(([toothLabel, surfaceData]) => {
          Object.entries(surfaceData).forEach(([surface, color]) => {
            const key = `${toothLabel}:${surface}`;
            toothSurfaceToDiagnosis[key] = selection.diagnosis;
          });
        });
      }
    });

    if (selectedDiagnosis) {
      Object.entries(selected).forEach(([toothLabel, surfaceData]) => {
        Object.entries(surfaceData).forEach(([surface, color]) => {
          const key = `${toothLabel}:${surface}`;
          toothSurfaceToDiagnosis[key] = selectedDiagnosis;
        });
      });
    }

    return toothSurfaceToDiagnosis;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const viewDischargeEntry = (entry, type, index) => {
    // Navigate to view page
    navigate(`/patients/${patientId}/dental-notes/discharge/${index}`, {
      state: {
        dischargeEntry: entry,
        odontogramType: type
      }
    });
  };

  // Render content for active tab
  const renderTabContent = () => {
    if (activeTab === 'initial') {
      return renderOdontogramEditor(
        'initial',
        initialSelected,
        initialSelectedDiagnosis,
        setInitialSelectedDiagnosis,
        initialCurrentNoteText,
        setInitialCurrentNoteText,
        initialGeneralNotes,
        setInitialGeneralNotes,
        initialAddedSelections,
        toggleInitialSelection,
        handleAddInitialSelection,
        handleRemoveInitialSelection,
        handleSaveInitial
      );
    } else if (activeTab === 'evolution') {
      return renderOdontogramEditor(
        'evolution',
        evolutionSelected,
        evolutionSelectedDiagnosis,
        setEvolutionSelectedDiagnosis,
        evolutionCurrentNoteText,
        setEvolutionCurrentNoteText,
        evolutionGeneralNotes,
        setEvolutionGeneralNotes,
        evolutionAddedSelections,
        toggleEvolutionSelection,
        handleAddEvolutionSelection,
        handleRemoveEvolutionSelection,
        handleSaveEvolution
      );
    } else if (activeTab === 'discharge') {
      return renderDischargeTab();
    }
  };

  const renderOdontogramEditor = (
    type,
    selected,
    selectedDiagnosis,
    setSelectedDiagnosis,
    currentNoteText,
    setCurrentNoteText,
    generalNotes,
    setGeneralNotes,
    addedSelections,
    toggleSelection,
    handleAddSelection,
    handleRemoveSelection,
    handleSave
  ) => {
    return (
      <div className="odontogram-editor-content">
        <div className="selection-controls">
          <div className="control-section">
            <h4>Seleccionar diagnóstico:</h4>
            <DiagnosisDropdown
              selectedDiagnosis={selectedDiagnosis}
              onDiagnosisSelect={(diagnosis) => {
                setSelectedDiagnosis(diagnosis);
                // Clear current selection when changing diagnosis
                if (type === 'initial') {
                  setInitialSelected({});
                  setInitialCurrentNoteText('');
                } else {
                  setEvolutionSelected({});
                  setEvolutionCurrentNoteText('');
                }
              }}
              placeholder="Buscar y seleccionar diagnóstico..."
            />
          </div>

          {selectedDiagnosis && (
            <div className="control-section">
              <h4>Detalles del diagnóstico:</h4>
              <div className="selected-diagnosis-info">
                <div className="diagnosis-header">
                  <span className="diagnosis-name">
                    <i className="fas fa-stethoscope"></i>
                    {selectedDiagnosis.name}
                  </span>
                  <span
                    className="diagnosis-color-dot"
                    style={{ backgroundColor: selectedDiagnosis.color }}
                    title={`Color: ${selectedDiagnosis.color}`}
                  ></span>
                </div>
                <div className="diagnosis-type">
                  <i className={`fas ${selectedDiagnosis.isCompleteTooth ? 'fa-tooth' : 'fa-layer-group'}`}></i>
                  {selectedDiagnosis.isCompleteTooth ? 'Aplica a diente completo' : 'Aplica por cara individual'}
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="odontogram-panel">
          <Odontogram
            mode={mode}
            selected={getAllSelections(type)}
            onToggle={toggleSelection}
            diagnosisMap={getDiagnosisMap(type)}
          />
        </div>

        <div className="note-and-selections">
          <div className="current-selection">
            <h3>Selección Actual</h3>
            <div className="current-note">
              <label>
                <i className="fas fa-comment-medical"></i>
                Nota específica para este diagnóstico:
              </label>
              <textarea
                placeholder="Descripción específica para este diagnóstico (opcional)..."
                value={currentNoteText}
                onChange={(e) => setCurrentNoteText(e.target.value)}
              />
            </div>

            <div className="current-selection-panel">
              {Object.keys(selected).length === 0 ? (
                <p className="muted">
                  {selectedDiagnosis
                    ? `Haz clic en ${selectedDiagnosis.isCompleteTooth ? 'cualquier superficie del diente' : 'las superficies específicas'} para marcarlas con el diagnóstico "${selectedDiagnosis.name}".`
                    : 'Primero selecciona un diagnóstico para comenzar a marcar dientes.'
                  }
                </p>
              ) : (
                <>
                  <div className="selection-grid">
                    {Object.entries(selected).map(([toothLabel, surfaceData]) => (
                      <div key={toothLabel} className="selection-group">
                        <div className="selection-group-header">
                          <span className="badge tooth">{toothLabel}</span>
                          <span className="group-count">{Object.keys(surfaceData).length}</span>
                        </div>
                        <div className="selection-chips">
                          {Object.entries(surfaceData).map(([surface, color]) => {
                            const surfaceName = getSurfaceName(toothLabel, surface);
                            return (
                              <button
                                key={`${toothLabel}:${surface}`}
                                className={`chip surface ${surface}`}
                                style={{
                                  backgroundColor: `${color}40`,
                                  borderColor: color
                                }}
                                onClick={() => toggleSelection(toothLabel, surface)}
                                title={`${surfaceName} (Quitar selección)`}
                              >
                                {surfaceName}
                                <span className="chip-close">×</span>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="add-selection-action">
                    <Button
                      variant="primary"
                      icon="fas fa-plus"
                      onClick={handleAddSelection}
                      disabled={!selectedDiagnosis || Object.keys(selected).length === 0}
                    >
                      Agregar Selección
                    </Button>
                  </div>
                </>
              )}
            </div>
          </div>

          <div className="added-selections">
            <h3>Selecciones Agregadas ({addedSelections.length})</h3>
            {addedSelections.length === 0 ? (
              <p className="muted">No hay selecciones agregadas aún.</p>
            ) : (
              <div className="added-selections-list">
                {addedSelections.map((selection) => (
                  <div key={selection.id} className="added-selection-item">
                    <div className="added-selection-header">
                      <div className="diagnosis-info">
                        <span className="diagnosis-name">
                          <i className="fas fa-stethoscope"></i>
                          {selection.diagnosis.name}
                        </span>
                      </div>
                      <button
                        className="btn btn-danger btn-sm"
                        onClick={() => handleRemoveSelection(selection.id)}
                        title="Eliminar selección"
                      >
                        <i className="fas fa-trash"></i>
                      </button>
                    </div>

                    <div className="selection-content">
                      <div className="teeth-selections">
                        {Object.entries(selection.selections).map(([toothLabel, surfaceData]) => (
                          <div key={toothLabel} className="tooth-selection">
                            <span className="tooth-label">{toothLabel}:</span>
                            <div className="surfaces">
                              {Object.keys(surfaceData).map(surface => {
                                const surfaceName = getSurfaceName(toothLabel, surface);
                                return (
                                  <span key={surface} className="surface-chip">
                                    {surfaceName}
                                  </span>
                                );
                              })}
                            </div>
                          </div>
                        ))}
                      </div>

                      {selection.noteText && (
                        <div className="selection-note">
                          <strong>Nota:</strong> {selection.noteText}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="general-notes-section">
          <h3>
            <i className="fas fa-file-medical-alt"></i>
            Notas Generales
          </h3>
          <p className="section-description">
            Estas notas se aplicarán a todo el odontograma {type === 'initial' ? 'inicial' : 'evolutivo'}.
          </p>
          <div className="general-notes-input">
            <textarea
              placeholder="Agregar observaciones generales, hallazgos importantes, recomendaciones, etc..."
              value={generalNotes}
              onChange={(e) => setGeneralNotes(e.target.value)}
              rows="4"
            />
            <div className="character-counter">
              {generalNotes.length} caracteres
            </div>
          </div>
        </div>

        <div className="page-actions">
          <Button
            variant="primary"
            icon={isSaving ? "fas fa-spinner fa-spin" : "fas fa-save"}
            onClick={handleSave}
            disabled={isSaving}
          >
            {isSaving ? 'Guardando...' : 'Guardar Odontograma'}
          </Button>
        </div>
      </div>
    );
  };

  const renderDischargeTab = () => {
    return (
      <div className="discharge-tab-content">
        <div className="discharge-header">
          <h3>Dar de Alta al Paciente</h3>
          <p>
            Los odontogramas se archivarán con la fecha actual y se limpiarán para comenzar un nuevo ciclo.
          </p>
          <Button
            variant="primary"
            icon="fas fa-check-circle"
            onClick={() => {
              if (!hasSavedOdontogramData()) {
                warning('Guarda el odontograma antes de dar de alta.');
                return;
              }
              setShowDischargeConfirmModal(true);
            }}
            disabled={isSaving || !hasSavedOdontogramData()}
          >
            Dar de Alta
          </Button>
        </div>

        <div className="discharge-history-section">
          <h3>
            <i className="fas fa-history"></i>
            Historial de Altas ({dischargeHistory.length})
          </h3>

          {dischargeHistory.length === 0 ? (
            <p className="muted">No hay altas registradas aún.</p>
          ) : (
            <div className="discharge-history-list">
              {dischargeHistory.map((entry, index) => (
                <div key={index} className="discharge-entry-card">
                  <div className="discharge-entry-header">
                    <h4>{formatDate(entry.date)}</h4>
                  </div>
                  <div className="discharge-entry-info">
                    <div className="discharge-entry-item clickable" onClick={() => viewDischargeEntry(entry, 'initial', index)}>
                      <div className="discharge-entry-content">
                        <i className="fas fa-notes-medical"></i>
                        <span>Odontograma Inicial: {entry.initial?.subNotes?.length || 0} diagnósticos</span>
                      </div>
                      <button className="view-icon-btn" title="Ver Odontograma Inicial">
                        <i className="fas fa-eye"></i>
                      </button>
                    </div>
                    <div className="discharge-entry-item clickable" onClick={() => viewDischargeEntry(entry, 'evolution', index)}>
                      <div className="discharge-entry-content">
                        <i className="fas fa-chart-line"></i>
                        <span>Odontograma Evolutivo: {entry.evolution?.subNotes?.length || 0} diagnósticos</span>
                      </div>
                      <button className="view-icon-btn" title="Ver Odontograma Evolutivo">
                        <i className="fas fa-eye"></i>
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Discharge confirmation modal */}
        {showDischargeConfirmModal && (
          <Modal
            isOpen={showDischargeConfirmModal}
            onClose={() => setShowDischargeConfirmModal(false)}
            title="¡Atención!"
            size="md"
          >
            <div className="discharge-confirm-content">
              <p className="warning-text">
                El odontograma inicial y de evolución serán archivados y ya no podrá editarlos. 
                El cambio es irreversible. ¿Desea continuar?
              </p>
              <div className="info-note">
                <i className="fas fa-info-circle"></i>
                <span>Los odontogramas archivados podrán consultarse en el historial, pero no se podrán modificar.</span>
              </div>
            </div>
            <div className="modal-actions">
              <Button
                variant="outline"
                onClick={() => setShowDischargeConfirmModal(false)}
              >
                Cancelar
              </Button>
              <Button
                variant="primary"
                icon="fas fa-check-circle"
                onClick={handleDischargePatient}
                disabled={isSaving}
              >
                Confirmar Alta
              </Button>
            </div>
          </Modal>
        )}
      </div>
    );
  };

  return (
    <div className="odontogram-tabs-container">
      <PatientSectionHeader
        patientId={patientId}
        patientName={patient ? `${patient.firstName} ${patient.lastName}` : null}
        sectionTitle="Odontograma"
        onBackClick={goBack}
      />

      {/* Mode selector (adult/pediatric) */}
      <div className={`mode-switch-odontogram ${hasSavedOdontogramData() ? 'disabled' : ''}`}>
        <label className={`mode-option ${mode === 'adult' ? 'active' : ''} ${hasSavedOdontogramData() ? 'disabled' : ''}`}>
          <input
            type="radio"
            name="mode"
            value="adult"
            checked={mode === 'adult'}
            onChange={() => handleModeChange('adult')}
            disabled={hasSavedOdontogramData()}
          />
          <i className="fas fa-user"></i>
          Adulto
        </label>
        <label className={`mode-option ${mode === 'pediatric' ? 'active' : ''} ${hasSavedOdontogramData() ? 'disabled' : ''}`}>
          <input
            type="radio"
            name="mode"
            value="pediatric"
            checked={mode === 'pediatric'}
            onChange={() => handleModeChange('pediatric')}
            disabled={hasSavedOdontogramData()}
          />
          <i className="fas fa-child"></i>
          Pediátrico
        </label>
      </div>

      {/* Tabs */}
      <div className="odontogram-tabs">
        <button
          className={`tab-button ${activeTab === 'initial' ? 'active' : ''}`}
          onClick={() => setActiveTab('initial')}
        >
          <i className="fas fa-notes-medical"></i>
          Odontograma Inicial
        </button>
        <button
          className={`tab-button ${activeTab === 'evolution' ? 'active' : ''}`}
          onClick={() => setActiveTab('evolution')}
        >
          <i className="fas fa-chart-line"></i>
          Odontograma Evolución
        </button>
        <button
          className={`tab-button ${activeTab === 'discharge' ? 'active' : ''}`}
          onClick={() => setActiveTab('discharge')}
        >
          <i className="fas fa-clipboard-check"></i>
          Dar de Alta
        </button>
      </div>

      {/* Active tab content */}
      <div className="tab-content">
        {loading && activeTab !== 'discharge' ? (
          <div className="loading-state">
            <i className="fas fa-spinner fa-spin"></i>
            <p>Cargando odontograma...</p>
          </div>
        ) : (
          renderTabContent()
        )}
      </div>

      {/* Toast notifications */}
      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </div>
  );
};

export default OdontogramTabs;
